import React, { type FC } from "react";
import { ListItem } from "@src/components/business-list";
import { BusinessLotValue, BusinessMeasureValue, BusinessStockObjValue } from "@src/components/business-data-display";
import { OverflowMarquee } from "@src/shared/ui";
import type { StockGatheringReserveItem } from "../../../../../../api/stockGatherings";
import type { StorePlaceRemainder } from "../../../../../../api/storePlaces";
import { formatOrderText } from "../../../../../StockFinishedGoodsReceipt/modules/Card/helpers/formatFinishedGoodsReceiptContent";

/** Пропсы {@link StockGatheringCompletionReserveListItem}. */
export interface StockGatheringCompletionReserveListItemProps {
    /** Резерв КВ. */
    reserve: StockGatheringReserveItem;
    /**
     * Порядковый номер в полном списке карточки (1-based title `Резерв №N`). \
     * В окне выбора после скана можно не передавать.
     */
    index?: number;
    /**
     * Выбор резерва.
     * @param reserveCode код резерва
     */
    onSelect: (reserveCode: number) => void;
}

/** Строка резерва КВ для списка карточки и окна неоднозначного выбора. */
export const StockGatheringCompletionReserveListItem: FC<StockGatheringCompletionReserveListItemProps> = ({
    reserve,
    index,
    onSelect,
}) => {
    const title = index == null ? "Резерв" : `Резерв №${index + 1}`;

    return (
        <ListItem
            title={title}
            props={[
                {
                    label: "Номенклатура",
                    value: <BusinessStockObjValue stockObj={reserve.Content?.StockObj} mode="wrap" />,
                },
                {
                    label: "Партия",
                    value: <BusinessLotValue lot={reserve.Lot} fallback="<Без партии>" />,
                },
                {
                    label: "Заказ",
                    value: <OverflowMarquee>{formatOrderText(reserve.Order)}</OverflowMarquee>,
                },
                {
                    label: "Ячейки размещения",
                    value: <OverflowMarquee>{formatReserveCells(reserve.StorePlaceRemainders)}</OverflowMarquee>,
                },
                {
                    label: "Кол-во",
                    value: <BusinessMeasureValue value={reserve.Qty} measure={reserve.Measure} />,
                },
            ]}
            onClick={() => {
                onSelect(reserve.Code);
            }}
        />
    );
};

/**
 * Форматирует ячейки, в которых находится партия резерва.
 * @param remainders остатки партии по местам хранения
 */
export function formatReserveCells(remainders: StorePlaceRemainder[] | undefined): string {
    const cellNames = remainders
        ?.map((remainder) => {
            const storePlace = remainder.StorePlace;
            return storePlace == null ? "" : `${storePlace.Num} - ${storePlace.SPIndex}`;
        })
        .filter((name) => name !== "");

    if (cellNames == null || cellNames.length === 0) {
        return "<Не размещена в ячейке>";
    }

    return cellNames.join(", ");
}
